import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import ScorecardForm from '../components/ScorecardForm';

const Scorecards = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [filter, setFilter] = useState({ match_id: '' });
  const queryClient = useQueryClient();

  const { data: scorecards = [], isLoading } = useQuery({
    queryKey: ['scorecards', filter],
    queryFn: () => {
      const cleanFilter = Object.fromEntries(
        Object.entries(filter).filter(([_, value]) => value && value.trim() !== '')
      );
      return api.getScorecards(cleanFilter);
    }
  });
  const { data: matches = [] } = useQuery({ queryKey: ['matches'], queryFn: api.getMatches });
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: api.getTeams });
  const { data: players = [] } = useQuery({ queryKey: ['players'], queryFn: api.getPlayers });

  const createMutation = useMutation({
    mutationFn: api.createScorecard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scorecards'] });
      setShowForm(false);
      toast.success('Scorecard created successfully!');
    },
    onError: () => toast.error('Failed to create scorecard'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updateScorecard(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scorecards'] });
      setShowForm(false);
      setEditingScorecard(null);
      toast.success('Scorecard updated successfully!');
    },
    onError: () => toast.error('Failed to update scorecard'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteScorecard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scorecards'] });
      toast.success('Scorecard deleted successfully!');
    },
    onError: () => toast.error('Failed to delete scorecard'),
  });

  const handleSubmit = (data) => {
    // This function is only called for editing existing scorecards
    // New scorecards are handled entirely by the ScorecardForm internally
    if (editingScorecard) {
      updateMutation.mutate({ id: editingScorecard.id, data });
    }
    // For new scorecards, the form handles everything internally and calls onCancel()
  };

  const handleEdit = (scorecard) => {
    setEditingScorecard(scorecard);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingScorecard(null);
    // Refresh the scorecards list when form is closed
    queryClient.invalidateQueries('scorecards');
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this scorecard?')) {
      deleteMutation.mutate(id);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const getMatchInfo = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    return match ? `Match #${match.match_number}` : 'Unknown Match';
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : 'Unknown Player';
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Scorecards</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-cricket-green text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          <span>Create Scorecard</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Match</label>
            <select
              value={filter.match_id}
              onChange={(e) => setFilter({ ...filter, match_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Matches</option>
              {matches.map(match => (
                <option key={match.id} value={match.id}>
                  {getMatchInfo(match.id)} - {getTeamName(match.team1_id)} vs {getTeamName(match.team2_id)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showForm ? (
        <ScorecardForm
          scorecard={editingScorecard}
          matches={matches}
          teams={teams}
          players={players}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      ) : (
        <div className="space-y-4">
        {scorecards.map((scorecard) => (
          <div key={scorecard.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-cricket-green rounded-full flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {getMatchInfo(scorecard.match_id)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getTeamName(scorecard.team1_innings.team_id)} vs {getTeamName(scorecard.team2_innings.team_id)}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(scorecard)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(scorecard.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Team 1 Innings */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800 text-center">
                  {getTeamName(scorecard.team1_innings.team_id)} - {scorecard.team1_innings.total_runs}/{scorecard.team1_innings.total_wickets}
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Overs:</span>
                    <span>{scorecard.team1_innings.total_overs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Extras:</span>
                    <span>{scorecard.team1_innings.extras?.total || 0}</span>
                  </div>
                </div>
                {scorecard.team1_innings.batting.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Batting</div>
                    <div className="space-y-1">
                      {scorecard.team1_innings.batting.slice(0, 3).map((batting, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          {getPlayerName(batting.player_id)}: {batting.runs}({batting.balls})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Team 2 Innings */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800 text-center">
                  {getTeamName(scorecard.team2_innings.team_id)} - {scorecard.team2_innings.total_runs}/{scorecard.team2_innings.total_wickets}
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Overs:</span>
                    <span>{scorecard.team2_innings.total_overs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Extras:</span>
                    <span>{scorecard.team2_innings.extras?.total || 0}</span>
                  </div>
                </div>
                {scorecard.team2_innings.batting.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Batting</div>
                    <div className="space-y-1">
                      {scorecard.team2_innings.batting.slice(0, 3).map((batting, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          {getPlayerName(batting.player_id)}: {batting.runs}({batting.balls})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Match Result */}
            {scorecard.is_match_completed && scorecard.match_result && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className={`text-center py-2 px-4 rounded-lg ${
                  scorecard.match_result.result_type === 'tie'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  <span className="font-semibold">
                    {scorecard.match_result.winner_id
                      ? `${getTeamName(scorecard.match_result.winner_id)} ${scorecard.match_result.result_description}`
                      : scorecard.match_result.result_description}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Toss Winner:</span>
                  <span>{getTeamName(scorecard.toss_winner)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Decision:</span>
                  <span className="capitalize">{scorecard.toss_decision}</span>
                </div>
                {scorecard.man_of_the_match && (
                  <div className="flex justify-between">
                    <span>Man of the Match:</span>
                    <span>{getPlayerName(scorecard.man_of_the_match)}</span>
                  </div>
                )}
                {!scorecard.is_match_completed && (
                  <div className="flex justify-between mt-2">
                    <span>Status:</span>
                    <span className="text-orange-600 font-medium">In Progress</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {!showForm && scorecards.length === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No scorecards found</h3>
          <p className="text-gray-500 mb-4">Create your first scorecard to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-cricket-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
          >
            Create Scorecard
          </button>
        </div>
      )}
    </div>
  );
};

export default Scorecards;
